import { withoutBlankFilterChoices } from "@/components/non_registrant/dashboard-column-filter-match";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { OrganizationDetailsSheet } from "@/components/non_registrant/organization-details-sheet";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { getNonRegistrantOrganizationsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type { NonRegistrantOrganizationListItemSchema } from "@/hera-api/client/types.gen";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useState } from "react";

const ORGANIZATION_ACCESSOR_KEYS = [
  "id",
  "email",
  "name",
  "company_name",
  "secondary_count",
] as const;

type OrganizationAccessorKeys = (typeof ORGANIZATION_ACCESSOR_KEYS)[number];
type OrganizationColumnProperties = ColumnProperties<
  NonRegistrantOrganizationListItemSchema,
  OrganizationAccessorKeys
>;

const ORGANIZATION_COLUMNS_WITHOUT_BLANK_FILTER = new Set<string>([
  "id",
  "email",
  "secondary_count",
]);

const buildOrganizationColumnProperties = (
  accessorKey: OrganizationAccessorKeys,
): OrganizationColumnProperties => {
  switch (accessorKey) {
    case "id":
      return {
        displayName: "ID",
        defaultVisible: false,
        getValue: (row) => row.id,
      };
    case "email":
      return {
        displayName: "Email",
        defaultVisible: true,
        getValue: (row) => row.email,
      };
    case "name":
      return {
        displayName: "Name",
        defaultVisible: true,
        getValue: (row) => row.name ?? "",
      };
    case "company_name":
      return {
        displayName: "Company",
        defaultVisible: true,
        getValue: (row) => row.company_name ?? "",
      };
    case "secondary_count":
      return {
        displayName: "Secondary Accounts",
        defaultVisible: true,
        getValue: (row) => String(row.secondary_count),
      };
    default: {
      const _exhaust: never = accessorKey;
      void _exhaust;
      return {
        displayName: String(accessorKey),
        defaultVisible: false,
        getValue: () => "",
      };
    }
  }
};

const getOrganizationColumnProperties = (
  accessorKey: OrganizationAccessorKeys,
): OrganizationColumnProperties =>
  withoutBlankFilterChoices(
    accessorKey,
    ORGANIZATION_COLUMNS_WITHOUT_BLANK_FILTER,
    buildOrganizationColumnProperties(accessorKey),
  );

interface DashboardOrganizationsProps {
  isAuthSetupComplete: boolean;
  jurisdictionIds: string[];
  /** When provided, sheet state is controlled by parent (e.g. setup page). Parent should render OrganizationDetailsSheet. */
  selectedOrganizationId?: string | null;
  onOrganizationSelect?: (id: string) => void;
  isOrganizationSheetOpen?: boolean;
  onOrganizationSheetOpenChange?: (open: boolean) => void;
  onOpenRegistrant?: (registrantId: string) => void;
  /** Page chrome rendered inside the list scroller so it scrolls with the rows. */
  leadingContent?: ReactNode;
}

export function DashboardOrganizations({
  isAuthSetupComplete,
  jurisdictionIds,
  selectedOrganizationId: controlledOrganizationId,
  onOrganizationSelect,
  isOrganizationSheetOpen: controlledSheetOpen,
  onOrganizationSheetOpenChange,
  onOpenRegistrant: _onOpenRegistrant,
  leadingContent,
}: DashboardOrganizationsProps) {
  const [internalOrganizationId, setInternalOrganizationId] = useState<
    string | null
  >(null);
  const [internalSheetOpen, setInternalSheetOpen] = useState(false);

  const isControlled =
    controlledOrganizationId !== undefined &&
    onOrganizationSelect !== undefined &&
    controlledSheetOpen !== undefined &&
    onOrganizationSheetOpenChange !== undefined;

  const selectedOrganizationId = isControlled
    ? (controlledOrganizationId ?? null)
    : internalOrganizationId;
  const isSheetOpen = isControlled ? controlledSheetOpen : internalSheetOpen;
  const setSelectedOrganizationId = isControlled
    ? (id: string | null) => id && onOrganizationSelect?.(id)
    : setInternalOrganizationId;
  const setIsSheetOpen = isControlled
    ? (onOrganizationSheetOpenChange ?? (() => {}))
    : setInternalSheetOpen;

  const {
    data: organizationsData,
    isLoading,
    error,
  } = useQuery({
    ...getNonRegistrantOrganizationsOptions({
      query: {
        include_deleted: true,
        jurisdiction_ids:
          jurisdictionIds.length > 0 ? jurisdictionIds : undefined,
      },
    }),
    enabled: isAuthSetupComplete,
  });

  const handleRowClick = useCallback(
    (organizationId: string) => {
      setSelectedOrganizationId(organizationId);
      setIsSheetOpen(true);
    },
    [setSelectedOrganizationId, setIsSheetOpen],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching organizations..." />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {leadingContent}
        <ErrorComponent
          title={getQueryErrorHeading(error)}
          message={getQueryErrorMessage(error)}
        />
      </div>
    );
  }

  const organizations = organizationsData?.organizations ?? [];

  const listChrome = <div className="space-y-4 pb-4">{leadingContent}</div>;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DashboardTable<
          NonRegistrantOrganizationListItemSchema,
          OrganizationAccessorKeys
        >
          leadingContent={listChrome}
          lockToViewport
          data={organizations}
          onRowClick={handleRowClick}
          accessorKeys={ORGANIZATION_ACCESSOR_KEYS}
          getColumnProperties={getOrganizationColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey="non-registrant-organizations"
          initialSortColumn="company_name"
          initialSortDirection="asc"
        />
      </div>

      {!isControlled && (
        <OrganizationDetailsSheet
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          organizationId={selectedOrganizationId}
          key={selectedOrganizationId}
        />
      )}
    </div>
  );
}
